import {
  GREEN_LIMIT,
  INTRO_PLACEHOLDERS,
  MAX_LENGTH,
  MIN_GOOD_LENGTH,
  ORANGE_LIMIT,
} from "./constants";
import { palette } from "../../../../../utils/palette";

export const isHtmlEmpty = (value: string): boolean => {
  const strippedValue = value.replace(/<(.|\n)*?>/g, "").trim();
  return strippedValue.length === 0;
};

export const normalizeSignatureHtml = (value?: string | null): string => {
  if (!value || isHtmlEmpty(value)) return "";
  return value.replace(/\s+/g, " ").trim();
};

export const calculateProgress = (length: number): number => {
  if (length <= 0) return 0;
  return Math.min((length / MAX_LENGTH) * 100, 100);
};

export const getLengthColor = (length: number): string => {
  if (length < MIN_GOOD_LENGTH) return "bg-amber-500";
  if (length <= GREEN_LIMIT) return "bg-green-500";
  if (length <= ORANGE_LIMIT) return "bg-amber-400";
  return "bg-red-500";
};

export const EMAIL = {
  filet: palette.gray300,
  texteSecondaire: palette.gray600,
  fondBloc: palette.gray50,
  fondCode: palette.gray100,
};

export const cleanHtmlForEmail = (html: string): string => {
  let result = html;

  result = result.replace(
    /<ul(?![^>]*style=)([^>]*)>/gi,
    (_match, attrs = "") =>
      `<ul${attrs} style="padding-left: 1.5em; list-style-type: disc;">`
  );
  result = result.replace(
    /<ol(?![^>]*style=)([^>]*)>/gi,
    (_match, attrs = "") =>
      `<ol${attrs} style="padding-left: 1.5em; list-style-type: decimal;">`
  );
  result = result.replace(
    /<li(?![^>]*style=)([^>]*)>/gi,
    (_match, attrs = "") =>
      `<li${attrs} style="display: list-item; margin-bottom: 0.5em;">`
  );

  result = result.replace(
    /<blockquote(?![^>]*style=)([^>]*)>/gi,
    (_match, attrs = "") =>
      `<blockquote${attrs} style="border-left: 4px solid ${EMAIL.filet}; margin: 1em 0; padding-left: 1em; color: ${EMAIL.texteSecondaire}; font-style: italic;">`
  );

  result = result.replace(
    /<pre(?![^>]*style=)([^>]*)>/gi,
    (_match, attrs = "") =>
      `<pre${attrs} style="font-family: monospace; background-color: ${EMAIL.fondBloc}; padding: 1em; border-radius: 4px; white-space: pre-wrap;">`
  );
  result = result.replace(
    /<code(?![^>]*style=)([^>]*)>/gi,
    (_match, attrs = "") =>
      `<code${attrs} style="font-family: monospace; background-color: ${EMAIL.fondCode}; padding: 0.2em 0.4em; border-radius: 3px;">`
  );

  result = result.replace(/(<br\s*\/?\s*>\s*){3,}/gi, "<br><br>");

  return result;
};

export const convertMarkdownQuotesToHtml = (text: string): string => {
  const lines = text.split("\n");
  let result = "";
  let inBlockquote = false;
  let blockquoteContent = "";

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (line.startsWith(">")) {
      const content = line.replace(/^>\s?/, "");

      if (!inBlockquote) {
        inBlockquote = true;
        blockquoteContent = content;
      } else {
        blockquoteContent += "\n" + content;
      }
    } else {
      if (inBlockquote) {
        result += `<blockquote style="border-left: 4px solid ${EMAIL.filet}; margin: 1em 0; padding-left: 1em; color: ${EMAIL.texteSecondaire}; font-style: italic;">${blockquoteContent}</blockquote>`;
        inBlockquote = false;
        blockquoteContent = "";
      }

      if (line.trim() !== "") {
        result += line + "\n";
      }
    }
  }

  if (inBlockquote) {
    result += `<blockquote style="border-left: 4px solid ${EMAIL.filet}; margin: 1em 0; padding-left: 1em; color: ${EMAIL.texteSecondaire}; font-style: italic;">${blockquoteContent}</blockquote>`;
  }

  return result.trim();
};

export const resolveIntroPlaceholders = (
  intro: string | null | undefined,
  userData?: Record<string, unknown>
): string => {
  if (!intro) {
    return "";
  }

  return INTRO_PLACEHOLDERS.reduce((acc, placeholder) => {
    const key = placeholder.replace("{{", "").replace("}}", "");
    const value =
      typeof userData?.[key] === "string" ? (userData?.[key] as string) : "";
    const pattern = new RegExp(placeholder, "g");
    return acc.replace(pattern, value);
  }, intro);
};

const escapeHtml = (value: string) =>
  value.replace(
    /[&<>"']/g,
    (char) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      }[char] as string)
  );

/** The HTML content of a message for one recipient: the values are escaped, as the server does at the send. */
export const resolveContentPlaceholders = (
  html: string,
  userData?: Record<string, unknown>
): string =>
  resolveIntroPlaceholders(
    html,
    Object.fromEntries(
      Object.entries(userData ?? {}).map(([key, value]) => [
        key,
        typeof value === "string" ? escapeHtml(value) : value,
      ])
    )
  );
